import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Plus,
  MessageSquare,
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle,
  Eye,
  Link,
  BarChart3,
  Home,
  ExternalLink,
  Edit3,
  Trash2,
  Search,
  Filter,
  X,
  Mail,
  ArrowUpDown,
  Flag,
  DollarSign,
  Loader2
} from 'lucide-react';
import NewApplicationModal from '../applications/NewApplicationModal';
import LinksPanel from './LinksPanel';

const MultiSelectFilter = ({ options, selected, onChange, testId, placeholder = 'All', activeLabel }) => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const toggle = (val) => {
    onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
  };

  const label = selected.length === 0
    ? placeholder
    : selected.length === 1
      ? options.find(o => o.value === selected[0])?.label || selected[0]
      : `${selected.length} ${activeLabel || 'Selected'}`;

  return (
    <div className="relative" ref={ref} data-testid={testId}>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-2 pl-3 pr-8 py-2.5 border rounded-lg text-sm cursor-pointer min-w-[170px] transition-all text-left ${
          selected.length > 0
            ? 'bg-teal-50 border-teal-300 text-teal-800'
            : 'bg-slate-50 border-slate-200 text-[#081120]'
        }`}
      >
        <Filter className="h-3.5 w-3.5 flex-shrink-0 opacity-50" />
        <span className="truncate">{label}</span>
      </button>
      {selected.length > 0 && (
        <button
          onClick={(e) => { e.stopPropagation(); onChange([]); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 z-10"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
      {open && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-slate-200 rounded-lg shadow-lg z-50 max-h-72 overflow-y-auto">
          <div className="p-2 border-b border-slate-100 flex items-center justify-between">
            <span className="text-xs font-medium text-slate-500">{placeholder}</span>
            {selected.length > 0 && (
              <button onClick={() => onChange([])} className="text-xs text-teal-600 hover:text-teal-800">Clear all</button>
            )}
          </div>
          {options.map(opt => (
            <label
              key={opt.value}
              className="flex items-center gap-2.5 px-3 py-2 hover:bg-slate-50 cursor-pointer text-sm"
              onClick={() => toggle(opt.value)}
            >
              <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                selected.includes(opt.value) ? 'bg-teal-600 border-teal-600' : 'border-slate-300'
              }`}>
                {selected.includes(opt.value) && (
                  <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <span className="text-slate-700">{opt.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};
import AIApplicationFlow from '../applications/AIApplicationFlow';
import ApplicationReview from '../applications/ApplicationReview';
import MessagingPanel from '../messaging/MessagingPanel';
import BrokerToolkit from '../toolkit/BrokerToolkit';
import DashboardAIAssistant from '../dashboard/DashboardAIAssistant';
import TaskManager from '../tasks/TaskManager';
import BrokerCommissions from '../commissions/BrokerCommissions';
import axios from 'axios';
import { toast } from 'sonner';

import { StatusBadge, getStatusRowBg, isAttentionStatus } from '../shared/StatusBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

import { getPipelineTab } from './UnderwriterDashboard';
import { productLabel, getForeclosureName } from '../applications/loanFieldConstants';

const BrokerDashboard = () => {
  const { user, getUserApplications, getDashboardStats, ws } = useAuth();
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showNewApp, setShowNewApp] = useState(false);
  const [resumeApp, setResumeApp] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedMessageAppId, setSelectedMessageAppId] = useState(null);
  const [previousTab, setPreviousTab] = useState(null);
  const [previousContext, setPreviousContext] = useState(null);
  const [inviteConfirmAppId, setInviteConfirmAppId] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [importantApps, setImportantApps] = useState({});
  const [reviewingApp, setReviewingApp] = useState(null);
  const [inviteLink, setInviteLink] = useState('');
  const [teamInfo, setTeamInfo] = useState({ has_sub_brokers: false, sub_brokers: [], all_descendant_ids: [] });
  const [brokerLookup, setBrokerLookup] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState([]);
  const [brokerFilter, setBrokerFilter] = useState([]);
  const [loanTypeFilter, setLoanTypeFilter] = useState([]);
  const [sortOrder, setSortOrder] = useState('newest');
  const [pipelineTab, setPipelineTab] = useState('active');
  const [mpIntakeModalOpen, setMpIntakeModalOpen] = useState(false);
  const [mpIntakeLink, setMpIntakeLink] = useState('');
  const [mpIntakeLoading, setMpIntakeLoading] = useState(false);
  const [mpIntakeError, setMpIntakeError] = useState('');

  const openMpIntakeLink = async () => {
    setMpIntakeModalOpen(true);
    if (mpIntakeLink) return;
    setMpIntakeLoading(true);
    setMpIntakeError('');
    try {
      const res = await axios.get(`${BACKEND_URL}/api/broker/mp-intake-link`);
      const token = res.data?.token;
      if (token) {
        // Always build the link from the current browser origin so it matches
        // whichever domain the broker is using (portal.quidity.fund, preview, etc.)
        setMpIntakeLink(`${window.location.origin}/mp-intake/${token}`);
      } else {
        setMpIntakeError('No intake token returned');
      }
    } catch (err) {
      setMpIntakeError(err?.response?.data?.detail || 'Failed to load intake link');
    } finally {
      setMpIntakeLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchUnreadCounts();
    // Fallback polling every 30s (WebSocket handles real-time)
    const interval = setInterval(fetchUnreadCounts, 30000);
    return () => clearInterval(interval);
  }, []);

  // WebSocket: refresh unread counts on new message, refresh data on status change
  useEffect(() => {
    if (!ws) return;
    const removeListener = ws.addListener((data) => {
      if (data.type === 'new_message') {
        fetchUnreadCounts();
      }
      if (data.type === 'status_change') {
        fetchData();
      }
    });
    return removeListener;
  }, [ws]);

  const fetchUnreadCounts = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const [convRes, impRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/messages/conversations`),
        axios.get(`${BACKEND_URL}/api/messages/important-count`)
      ]);
      const counts = {};
      (convRes.data.conversations || []).forEach(c => {
        if (c.unread_count > 0) counts[c.application_id] = c.unread_count;
      });
      setUnreadCounts(counts);
      const impMap = {};
      (impRes.data.application_ids || []).forEach(id => { impMap[id] = true; });
      setImportantApps(impMap);
    } catch (error) { /* silent */ }
  };

  const fetchData = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const [apps, statsData, teamRes] = await Promise.all([
        getUserApplications(),
        getDashboardStats(),
        axios.get(`${BACKEND_URL}/api/broker/my-team`).catch(() => ({ data: { has_sub_brokers: false, sub_brokers: [], all_descendant_ids: [] } })),
      ]);
      setApplications(apps);
      setStats(statsData);
      const team = teamRes.data || { has_sub_brokers: false, sub_brokers: [], all_descendant_ids: [] };
      setTeamInfo(team);
      // Build broker name lookup if has sub-brokers
      if (team.has_sub_brokers) {
        const lookup = {};
        lookup[user.id] = user.name || 'You';
        team.sub_brokers.forEach(b => { lookup[b.id] = b.name; });
        setBrokerLookup(lookup);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateBorrowerLink = async (appId) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.post(`${BACKEND_URL}/api/borrower-links?broker_id=${user.id}&application_id=${appId}&mode=status`);
      const link = `${window.location.origin}/borrower/${response.data.link_token}`;
      try {
        await navigator.clipboard.writeText(link);
        alert(`Borrower link generated and copied to clipboard!\n\n${link}`);
      } catch (clipboardError) {
        const textArea = document.createElement('textarea');
        textArea.value = link;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        alert(`Borrower link generated and copied!\n\n${link}`);
      }
    } catch (error) {
      console.error('Error generating link:', error);
    }
  };

  const openMessagesForApp = (appId) => {
    const ctx = { tab: activeTab };
    if (reviewingApp) { ctx.reviewApp = reviewingApp; setReviewingApp(null); }
    setPreviousTab(activeTab);
    setPreviousContext(ctx);
    setSelectedMessageAppId(appId);
    setActiveTab('messages');
  };

  const handleBackFromMessages = () => {
    if (previousContext) {
      setActiveTab(previousContext.tab || 'dashboard');
      if (previousContext.reviewApp) setReviewingApp(previousContext.reviewApp);
    } else {
      setActiveTab(previousTab || 'dashboard');
    }
    setPreviousTab(null);
    setPreviousContext(null);
    setSelectedMessageAppId(null);
  };

  const sendBorrowerInvite = async (appId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/email/borrower-invite/${appId}`);
      toast.success('Borrower invite email sent!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invite');
    }
    setInviteConfirmAppId(null);
  };

  const getBusinessName = (app) =>
    app.term_loan_data?.legal_corporate_name || app.term_loan_data?.business_name || app.term_loan_10yr_data?.legal_corporate_name || app.term_loan_10yr_data?.business_name || app.term_loc_hybrid_data?.legal_corporate_name || app.term_loc_hybrid_data?.business_name || app.growth_term_loan_data?.business_name || app.term_loan_3_5yr_data?.legal_corporate_name || app.term_loan_3_5yr_data?.business_name || app.sba_loan_data?.business_legal_name || app.sba_loan_data?.business_name || app.line_of_credit_data?.business_name || app.equipment_financing_data?.business_name || app.merchant_processing_data?.company_name || app.merchant_processing_data?.business_legal_name || app.merchant_processing_data?.business_name || app.merchant_pre_app_data?.legal_business_name || app.merchant_pre_app_data?.dba_name || app.jumbo_term_loan_data?.business_legal_name || app.jumbo_term_loan_data?.business_dba_name || app.real_estate_financing_data?.legal_name_of_business || app.debt_consulting_data?.company_legal_name || app.debt_consulting_data?.company_dba || app.debt_consulting_data?.legal_business_name || app.debt_consulting_data?.dba || app.family_office_data?.legal_business_name || app.family_office_data?.dba || getForeclosureName(app) || (app.transportation_factoring_data?.first_name && app.transportation_factoring_data?.last_name ? `${app.transportation_factoring_data.first_name} ${app.transportation_factoring_data.last_name}` : null) || app.transportation_factoring_data?.first_name || app.business_name || app.applicant_name || 'Business Application';

  const getLoanAmount = (app) =>
    app.term_loan_data?.funding_amount_requested || app.term_loan_10yr_data?.funding_amount_requested || app.term_loc_hybrid_data?.funding_amount_requested || app.term_loc_hybrid_data?.total_loan_amount || app.term_loan_3_5yr_data?.funding_amount_requested || app.term_loan_3_5yr_data?.total_loan_amount || app.sba_loan_data?.loan_requested_amount || app.line_of_credit_data?.requested_line_of_credit_amount || app.equipment_financing_data?.amount_requested || app.merchant_processing_data?.amount_requested || app.jumbo_term_loan_data?.amount_requested || app.real_estate_financing_data?.financing_amount_requested || app.debt_consulting_data?.amount_of_debt_to_refinance || app.debt_consulting_data?.requested_funding_amount || app.family_office_data?.requested_funding_amount || app.foreclosure_bailout_data?.loan_amount || 0;

  const STATUS_OPTIONS = [
    { value: 'all', label: 'All Statuses' },
    { value: 'incomplete', label: 'Incomplete' },
    { value: 'submitted', label: 'Submitted' },
    { value: 'submission_complete', label: 'Submission Complete' },
    { value: 'prequalified', label: 'Prequalified' },
    { value: 'borrower_accepts_offer', label: 'Borrower Accepts' },
    { value: 'borrower_declines_offer', label: 'Borrower Declines' },
    { value: 'under_review', label: 'Under Review' },
    { value: 'pending_docs', label: 'Pending Docs/Info' },
    { value: 'approved', label: 'Approved' },
    { value: 'declined', label: 'Declined' },
    { value: 'withdrawn', label: 'Withdrawn' },
    { value: 'closing', label: 'Closing' },
    { value: 'closed', label: 'Closed' },
    { value: 'funded', label: 'Funded' },
    { value: 'broker_attention_needed', label: 'Broker Attention Needed' },
    { value: 'file_ready_for_underwriter', label: 'File Ready for UW' },
    { value: 'underwriter_attention_needed', label: 'UW Attention Needed' },
    // Merchant Processing-specific
    { value: 'mp_quote_issued', label: 'Quote Issued (MP)' },
    { value: 'mp_merchant_accepts_quote', label: 'Merchant Accepts Quote (MP)' },
    { value: 'mp_please_collect_digital_mpa', label: 'Please Collect Digital MPA (MP)' },
    { value: 'mp_ready_for_onboarding', label: 'Ready for Onboarding (MP)' },
    { value: 'mp_onboarding', label: 'Onboarding (MP)' },
    { value: 'mp_activated', label: 'Activated (MP)' },
    { value: 'mp_account_approved', label: 'Account Approved (MP)' },
    { value: 'mp_account_declined', label: 'Account Declined (MP)' },
  ];

  const LOAN_TYPE_OPTIONS = [
    { value: 'all', label: 'All Loan Types' },
    { value: 'sba_loan', label: 'SBA Loan' },
    { value: 'term_loan', label: 'Term Loan' },
    { value: 'line_of_credit', label: 'Line of Credit' },
    { value: 'equipment_financing', label: 'Equipment Financing' },
    { value: 'term_loan_3_5yr', label: 'EZ Term Loan 3-5 Yr' },
    { value: 'term_loan_10yr', label: 'Term Loan (10yr)' },
    { value: 'term_loc_hybrid', label: 'TL / LOC Hybrid' },
    { value: 'merchant_processing', label: 'Merchant Processing' },
    { value: 'jumbo_term_loan', label: 'Jumbo Term Loan' },
    { value: 'transportation_factoring', label: 'Transportation Factoring' },
    { value: 'real_estate_financing', label: 'Real Estate Financing' },
    { value: 'debt_consulting', label: 'Debt Consolidation' },
    { value: 'foreclosure_bailout', label: 'Foreclosure Bailout' },
    { value: 'family_office', label: 'Family Office Programs' },
  ];

  const brokerOptions = React.useMemo(() => {
    const names = new Map();
    applications.forEach(app => {
      const bId = app.broker_id || app.submitted_by;
      const bName = brokerLookup[bId] || brokerLookup[app.submitted_by] || app.submitted_by_name || '';
      if (bId && bName) names.set(bId, bName);
    });
    return [{ value: 'all', label: 'All Brokers' }, ...[...names.entries()].map(([id, name]) => ({ value: id, label: name }))];
  }, [applications, brokerLookup]);

  const pipelineCounts = React.useMemo(() => {
    const c = { all: 0, active: 0, unsubmitted: 0, funded: 0, archived: 0 };
    applications.forEach(app => { c[getPipelineTab(app)] += 1; });
    c.all = applications.length;
    return c;
  }, [applications]);

  const filteredApplications = applications.filter(app => {
    if (pipelineTab !== 'all' && getPipelineTab(app) !== pipelineTab) return false;
    if (statusFilter.length > 0 && !statusFilter.includes(app.status)) return false;
    if (loanTypeFilter.length > 0 && !loanTypeFilter.includes(app.product_type || '')) return false;
    if (brokerFilter.length > 0) {
      const bId = app.broker_id || app.submitted_by;
      if (!brokerFilter.includes(bId)) return false;
    }
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const name = getBusinessName(app).toLowerCase();
    const broker = (brokerLookup[app.broker_id] || brokerLookup[app.submitted_by] || '').toLowerCase();
    const amount = String(getLoanAmount(app) || '');
    const type = (app.product_type || '').replace(/_/g, ' ').toLowerCase();
    return name.includes(q) || broker.includes(q) || amount.includes(q) || type.includes(q);
  });

  const sortedApplications = React.useMemo(() => {
    // Bucket priority (low number = appears first / top):
    //   0    = broker_attention_needed             (top)
    //   0.5  = submitted_from_link owned by me     (per user request, only in owner's view)
    //   1    = submitted / file_ready_for_underwriter (next)
    //   2    = everything else (sorted by last-touched/updated date)
    //   3    = incomplete                          (just above declined/withdrawn)
    //   4    = declined / withdrawn / borrower_declines_offer (bottom)
    const getBucket = (app) => {
      const status = app.status;
      if (status === 'broker_attention_needed') return 0;
      if (status === 'submitted_from_link' && ((app.broker_id === user?.id) || (app.submitted_by === user?.id))) return 0.5;
      if (status === 'submitted' || status === 'file_ready_for_underwriter') return 1;
      if (status === 'incomplete') return 3;
      if (status === 'declined' || status === 'withdrawn' || status === 'borrower_declines_offer') return 4;
      return 2;
    };
    return [...filteredApplications].sort((a, b) => {
      const ba = getBucket(a);
      const bb = getBucket(b);
      if (ba !== bb) return ba - bb;
      // Within the same bucket: use last-touched / updated date (fall back to created_at).
      const dateA = new Date(a.updated_at || a.created_at || 0).getTime();
      const dateB = new Date(b.updated_at || b.created_at || 0).getTime();
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });
  }, [filteredApplications, sortOrder, user?.id]);

  const handleAIAction = useCallback((action) => {
    if (!action) return;
    const { type, value } = action;
    switch (type) {
      case 'navigate_tab':
        setActiveTab(value);
        break;
      case 'open_review': {
        const app = applications.find(a => a.id === value);
        if (app) setReviewingApp(app);
        break;
      }
      case 'filter_status':
        setActiveTab('dashboard');
        setStatusFilter(value && value !== 'all' ? [value] : []);
        break;
      case 'filter_loan_type':
        setActiveTab('dashboard');
        setLoanTypeFilter(value && value !== 'all' ? [value] : []);
        break;
      case 'filter_broker':
        setActiveTab('dashboard');
        setBrokerFilter(value && value !== 'all' ? [value] : []);
        break;
      case 'search':
        setActiveTab('dashboard');
        setSearchQuery(value || '');
        break;
      case 'clear_filters':
        setSearchQuery('');
        setStatusFilter([]);
        setLoanTypeFilter([]);
        setBrokerFilter([]);
        break;
      case 'refresh_data':
        fetchData();
        break;
      default:
        break;
    }
  }, [applications]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div>
      <Tabs value={activeTab} onValueChange={(val) => { setActiveTab(val); if (val !== 'messages') { setSelectedMessageAppId(null); setPreviousTab(null); setPreviousContext(null); } }} className="w-full">
        {/* Dark zone: Tabs + Stats */}
        <div className="bg-[#081120] pt-6 pb-16 px-8">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-semibold text-white tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Welcome back, {user.name}!</h2>
                <p className="text-white/50 text-sm mt-1">Manage your loan applications and access professional tools</p>
                {user.company_name && <p className="text-white/30 text-xs mt-0.5 tracking-wide">{user.company_name}</p>}
              </div>
              <div className="flex items-center gap-4">
                <TabsList className="bg-white/10 p-1 rounded-xl border border-white/10">
                  <TabsTrigger value="dashboard" className="flex items-center gap-1.5 rounded-lg text-white/60 data-[state=active]:bg-white/15 data-[state=active]:text-white" data-testid="broker-tab-dashboard">
                    <Home className="h-4 w-4" /><span>Dashboard</span>
                  </TabsTrigger>
                  <TabsTrigger value="messages" className="flex items-center gap-1.5 rounded-lg text-white/60 data-[state=active]:bg-white/15 data-[state=active]:text-white" data-testid="broker-tab-messages">
                    <MessageSquare className="h-4 w-4" /><span>Messages</span>
                  </TabsTrigger>
                  <TabsTrigger value="toolkit" className="flex items-center gap-1.5 rounded-lg text-white/60 data-[state=active]:bg-white/15 data-[state=active]:text-white" data-testid="broker-tab-toolkit">
                    <BarChart3 className="h-4 w-4" /><span>Broker Toolkit</span>
                  </TabsTrigger>
                  <TabsTrigger value="tasks" className="flex items-center gap-1.5 rounded-lg text-white/60 data-[state=active]:bg-white/15 data-[state=active]:text-white" data-testid="broker-tab-tasks">
                    <span>Tasks</span>
                  </TabsTrigger>
                  <TabsTrigger value="links" className="flex items-center gap-1.5 rounded-lg text-white/60 data-[state=active]:bg-white/15 data-[state=active]:text-white" data-testid="broker-tab-links">
                    <Link className="h-4 w-4" /><span>Links</span>
                  </TabsTrigger>
                  {user.commissions_enabled && (
                    <TabsTrigger value="commissions" className="flex items-center gap-1.5 rounded-lg text-white/60 data-[state=active]:bg-white/15 data-[state=active]:text-white" data-testid="broker-tab-commissions">
                      <DollarSign className="h-4 w-4" /><span>Commissions</span>
                    </TabsTrigger>
                  )}
                </TabsList>
                <div className="flex items-center gap-2">
                  <button onClick={() => setShowNewApp(true)} className="flex items-center gap-2 bg-[#66C2CA] hover:bg-[#5EDEE5] text-[#081120] font-bold px-5 py-2.5 rounded-lg shadow-md transition-all duration-200 text-sm" data-testid="new-application-btn">
                    <Plus className="h-4 w-4" /> New Application
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats cards — overlapping dark/light zone */}
        <div className="max-w-[1400px] mx-auto px-8 -mt-10 relative z-10 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              { label: 'Total Applications', value: applications.length, accent: '#66C2CA' },
              { label: 'Incomplete', value: applications.filter(a => a.status === 'incomplete').length, accent: '#F59E0B' },
              { label: 'Waiting for Broker', value: applications.filter(a => ['broker_attention_needed', 'pending_docs'].includes(a.status)).length, accent: '#EF4444' },
              { label: 'Waiting for UW', value: applications.filter(a => ['underwriter_attention_needed', 'file_ready_for_underwriter'].includes(a.status)).length, accent: '#A855F7' },
            ].map((stat, i) => (
              <div key={i} className="bg-[#162538] rounded-xl p-5 border border-white/10 hover:-translate-y-0.5 transition-all duration-300"
                style={{ boxShadow: '0 8px 32px rgba(8,17,32,0.3)' }}>
                <p className="text-[10px] font-semibold tracking-widest uppercase text-white/40">{stat.label}</p>
                <p className="text-2xl font-bold tracking-tighter mt-2" style={{ color: stat.accent, fontFamily: 'Outfit, sans-serif' }} data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g,'-')}`}>{stat.value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Light zone: Content */}
        <div className="max-w-[1400px] mx-auto px-8 pb-8">
        <TabsContent value="dashboard">
          <div className="bg-white rounded-xl border border-slate-200" style={{ boxShadow: '0 4px 24px rgba(8,17,32,0.04)' }}>
            {/* Pipeline tabs — Active / Unsubmitted / Funded / Archived / All */}
            <div className="px-6 pt-5 border-b border-slate-100">
              <div className="flex items-center gap-1" role="tablist" aria-label="Pipeline">
                {[
                  { id: 'active',      label: 'Active',      testid: 'pipeline-tab-active' },
                  { id: 'unsubmitted', label: 'Unsubmitted', testid: 'pipeline-tab-unsubmitted' },
                  { id: 'funded',      label: 'Funded',      testid: 'pipeline-tab-funded' },
                  { id: 'archived',    label: 'Archived',    testid: 'pipeline-tab-archived' },
                  { id: 'all',         label: 'All',         testid: 'pipeline-tab-all' },
                ].map(t => {
                  const isActive = pipelineTab === t.id;
                  return (
                    <button
                      key={t.id}
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setPipelineTab(t.id)}
                      data-testid={t.testid}
                      className={`relative px-4 py-2.5 text-sm font-medium transition-colors ${isActive ? 'text-[#081120]' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                      {t.label}
                      <span className={`ml-2 inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[11px] font-semibold ${isActive ? 'bg-[#66C2CA]/20 text-[#0f766e]' : 'bg-slate-100 text-slate-500'}`}>
                        {pipelineCounts[t.id]}
                      </span>
                      {isActive && (
                        <span className="absolute left-2 right-2 -bottom-px h-0.5 bg-[#66C2CA] rounded-t-full" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="p-6 pb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[#081120]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {pipelineTab === 'active' && 'Active Applications'}
                  {pipelineTab === 'unsubmitted' && 'Unsubmitted Applications'}
                  {pipelineTab === 'funded' && 'Funded Applications'}
                  {pipelineTab === 'archived' && 'Archived Applications'}
                  {pipelineTab === 'all' && 'All Applications'}
                </h3>
                <span className="text-xs font-semibold tracking-widest text-slate-400 uppercase">{filteredApplications.length} of {pipelineCounts[pipelineTab]}</span>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search by business name, broker, loan type..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-8 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-[#081120] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#66C2CA]/40 focus:border-[#66C2CA]/60 transition-all"
                    data-testid="broker-search-input"
                  />
                  {searchQuery && (
                    <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <MultiSelectFilter
                  options={STATUS_OPTIONS.filter(o => o.value !== 'all')}
                  selected={statusFilter}
                  onChange={setStatusFilter}
                  testId="broker-status-filter"
                  placeholder="All Statuses"
                  activeLabel="Statuses"
                />
                <MultiSelectFilter
                  options={LOAN_TYPE_OPTIONS.filter(o => o.value !== 'all')}
                  selected={loanTypeFilter}
                  onChange={setLoanTypeFilter}
                  testId="broker-loan-type-filter"
                  placeholder="All Programs"
                  activeLabel="Programs"
                />
                {brokerOptions.length > 2 && (
                  <MultiSelectFilter
                    options={brokerOptions.filter(o => o.value !== 'all')}
                    selected={brokerFilter}
                    onChange={setBrokerFilter}
                    testId="broker-broker-filter"
                    placeholder="All Brokers"
                    activeLabel="Brokers"
                  />
                )}
                <div className="relative">
                  <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                  <select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    className="pl-9 pr-8 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-[#081120] focus:outline-none focus:ring-2 focus:ring-[#66C2CA]/40 focus:border-[#66C2CA]/60 appearance-none cursor-pointer min-w-[150px] transition-all"
                    data-testid="broker-sort-order"
                  >
                    <option value="newest">Latest First</option>
                    <option value="oldest">Earliest First</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="px-6 pb-6">
              {filteredApplications.length === 0 ? (
                <div className="text-center py-12">
                  <Plus className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                  <h3 className="text-base font-medium text-[#081120] mb-1">
                    {applications.length === 0 ? 'No applications yet' : 'No matching applications'}
                  </h3>
                  <p className="text-sm text-slate-500 mb-4">
                    {applications.length === 0 ? 'Create your first application to get started.' : 'Try adjusting your search or filter.'}
                  </p>
                  {applications.length === 0 ? (
                    <button onClick={() => setShowNewApp(true)} className="bg-[#66C2CA] hover:bg-[#5EDEE5] text-[#081120] font-bold px-5 py-2.5 rounded-lg shadow-md transition-all text-sm" data-testid="create-first-app-btn">
                      <Plus className="h-4 w-4 mr-2 inline" /> Create First Application
                    </button>
                  ) : (
                    <button onClick={() => { setSearchQuery(''); setStatusFilter([]); setLoanTypeFilter([]); setBrokerFilter([]); }} className="bg-[#081120] hover:bg-[#162538] text-white font-semibold text-sm px-5 py-2 rounded-lg transition-all" data-testid="broker-clear-filters">
                      Clear Filters
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {(() => {
                    const renderApp = (app, isGrouped = false) => {
                      const isIncomplete = app.status === 'incomplete';
                      const statusBg = getStatusRowBg(app.status);
                      const pulseClass = isAttentionStatus(app.status) ? 'animate-attention-pulse' : '';
                      const isAttention = isAttentionStatus(app.status) && !isIncomplete;
                      const isUwAttn = app.status === 'underwriter_attention_needed' || app.status === 'file_ready_for_underwriter';
                      const attnBtnClass = isAttention
                        ? (isUwAttn ? 'border-white/80 text-white hover:bg-white hover:text-purple-700' : 'border-white/80 text-white hover:bg-white hover:text-red-700')
                        : '';
                      const attnBadgeClass = isAttention
                        ? (isUwAttn ? 'bg-white/90 text-purple-700 border-white/50 font-semibold' : 'bg-white/90 text-red-700 border-white/50 font-semibold')
                        : '';
                      return (
                      <div key={app.id} className={`p-4 ${isGrouped ? 'pl-8' : ''} ${pulseClass} ${isIncomplete ? 'border-2 border-dashed border-amber-400 bg-amber-50 ring-1 ring-amber-200' : statusBg} ${!isIncomplete ? 'border border-slate-200/60' : ''} rounded-lg hover:border-slate-300 transition-colors`} data-testid={`app-row-${app.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1.5">
                            <h3 className={`font-medium truncate max-w-[200px] sm:max-w-none ${isAttention ? 'text-white' : 'text-slate-800'}`}>
                              {getBusinessName(app)}
                            </h3>
                            {importantApps[app.id] && (
                              <span className="flex items-center gap-1 animate-attention-pulse" title="Has unread important messages">
                                <Flag className="h-3.5 w-3.5 text-yellow-500" fill="currentColor" />
                                <span className="text-[10px] font-bold text-yellow-600">IMPORTANT</span>
                              </span>
                            )}
                            {isIncomplete ? (
                              <Badge className="bg-amber-100 text-amber-800 border border-amber-300 text-[10px]">Incomplete</Badge>
                            ) : (
                              <StatusBadge status={app.status} productType={app.product_type} />
                            )}
                            {app.status === 'declined' && app.decline_reason && (
                              <span className="text-[11px] text-red-200 italic truncate max-w-[300px]">— {app.decline_reason}</span>
                            )}
                            {app.status === 'borrower_declines_offer' && app.borrower_decline_reason && (
                              <span className="text-[11px] text-red-200 italic truncate max-w-[300px]">— {app.borrower_decline_reason}</span>
                            )}
                            {app.status === 'withdrawn' && app.withdraw_reason && (
                              <span className="text-[11px] text-slate-400 italic truncate max-w-[300px]">— {app.withdraw_reason}</span>
                            )}
                            <Badge variant="outline" className={`whitespace-nowrap ${app.status === 'declined' || app.status === 'borrower_declines_offer' || app.status === 'withdrawn' ? 'border-slate-500 text-slate-200' : isAttention ? attnBadgeClass : ''}`}>{productLabel(app.product_type).toUpperCase()}</Badge>
                            {app.missing_documents && app.missing_documents.length > 0 && (
                              <Badge className={`text-[10px] ${isAttention ? attnBadgeClass : 'bg-orange-50 text-orange-700 border border-orange-200'}`}>
                                {app.missing_documents.length} doc{app.missing_documents.length > 1 ? 's' : ''} needed
                              </Badge>
                            )}
                          </div>
                          <div className={`flex items-center gap-3 text-sm flex-wrap ${isAttention ? 'text-red-100' : 'text-slate-600'}`}>
                            <span>${(getLoanAmount(app) || 0).toLocaleString()}</span>
                            <span>{new Date(app.created_at).toLocaleDateString()}</span>
                            {teamInfo.has_sub_brokers && (
                              <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded" data-testid={`app-broker-${app.id}`}>
                                Broker: {brokerLookup[app.broker_id] || brokerLookup[app.submitted_by] || 'Unknown'}
                              </span>
                            )}
                            {app.document_link && (
                              <a href={/^(https?:\/\/|mailto:|tel:)/i.test(String(app.document_link).trim()) ? app.document_link : `https://${String(app.document_link).trim().replace(/^\/+/, '')}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center gap-1">
                                <ExternalLink className="h-3 w-3" /> Docs
                              </a>
                            )}
                          </div>
                        </div>
                        <div className={`flex items-center gap-1.5 flex-wrap mt-3 pt-3 ${isAttention ? (isUwAttn ? 'border-t border-purple-400/40' : 'border-t border-red-400/40') : 'border-t border-slate-100'}`}>
                          {isIncomplete ? (
                            <>
                              <button onClick={() => setResumeApp(app)} className="bg-amber-500 hover:bg-amber-600 text-white font-semibold text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 transition-all" data-testid={`resume-btn-${app.id}`}>
                                <Edit3 className="h-3.5 w-3.5" /> Continue
                              </button>
                              <button onClick={() => generateBorrowerLink(app.id)} className="h-8 px-3 flex items-center gap-1.5 rounded-lg border-2 border-[#081120] text-[#081120] hover:bg-[#081120] hover:text-white text-xs font-semibold transition-all" data-testid={`share-btn-${app.id}`}>
                                <Link className="h-3.5 w-3.5" /> Share
                              </button>
                              <button onClick={() => setInviteConfirmAppId(app.id)} className="h-8 px-3 flex items-center gap-1.5 rounded-lg border-2 border-teal-600 text-teal-600 hover:bg-teal-600 hover:text-white text-xs font-semibold transition-all" data-testid={`invite-btn-${app.id}`}>
                                <Mail className="h-3.5 w-3.5" /> Invite
                              </button>
                              <button onClick={async () => {
                                if (!window.confirm('Delete this incomplete application?')) return;
                                try {
                                  await axios.delete(`${BACKEND_URL}/api/applications/${app.id}`);
                                  fetchData();
                                } catch (err) { console.error('Delete failed:', err); }
                              }} className={`h-8 px-3 flex items-center gap-1.5 rounded-lg border-2 text-xs font-semibold transition-all ${isAttention ? attnBtnClass : 'border-red-500 text-red-500 hover:bg-red-500 hover:text-white'}`} data-testid={`delete-btn-${app.id}`}>
                                <Trash2 className="h-3.5 w-3.5" /> Delete
                              </button>
                            </>
                          ) : (
                            <>
                              <button onClick={() => setReviewingApp(app)} className={`h-8 px-3 flex items-center gap-1.5 rounded-lg border-2 text-xs font-semibold transition-all ${isAttention ? attnBtnClass : 'border-[#081120] text-[#081120] hover:bg-[#081120] hover:text-white'}`} data-testid={`review-btn-${app.id}`}>
                                <Eye className="h-3.5 w-3.5" /> Review
                              </button>
                              <button onClick={() => generateBorrowerLink(app.id)} className={`h-8 px-3 flex items-center gap-1.5 rounded-lg border-2 text-xs font-semibold transition-all ${isAttention ? attnBtnClass : 'border-[#081120] text-[#081120] hover:bg-[#081120] hover:text-white'}`} data-testid={`share-btn-${app.id}`}>
                                <Link className="h-3.5 w-3.5" /> Share
                              </button>
                              <button onClick={() => setInviteConfirmAppId(app.id)} className={`h-8 px-3 flex items-center gap-1.5 rounded-lg border-2 text-xs font-semibold transition-all ${isAttention ? attnBtnClass : 'border-teal-600 text-teal-600 hover:bg-teal-600 hover:text-white'}`} data-testid={`invite-btn-${app.id}`}>
                                <Mail className="h-3.5 w-3.5" /> Invite
                              </button>
                            </>
                          )}
                          <button onClick={() => openMessagesForApp(app.id)} className={`relative h-8 px-3 flex items-center gap-1.5 rounded-lg border-2 text-xs font-semibold transition-all ${isAttention ? attnBtnClass : 'border-[#081120] text-[#081120] hover:bg-[#081120] hover:text-white'}`} data-testid={`msg-btn-${app.id}`}>
                            <MessageSquare className="h-3.5 w-3.5" /> Msgs
                            {unreadCounts[app.id] > 0 && (
                              <span className="absolute -top-1.5 -right-1.5 h-4 min-w-[16px] px-1 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold" data-testid={`app-unread-${app.id}`}>
                                {unreadCounts[app.id]}
                              </span>
                            )}
                          </button>
                        </div>
                      </div>
                    );
                    };

                    return (
                      <>
                        {(() => {
                          // Render in sortedApplications order. When we hit the
                          // first member of a group, render the group header +
                          // ALL group members inline. Subsequent group members
                          // are skipped so the group stays at its highest-priority
                          // position in the sort.
                          const renderedGroups = new Set();
                          const nodes = [];
                          sortedApplications.forEach(app => {
                            if (app.application_group_id) {
                              if (renderedGroups.has(app.application_group_id)) return;
                              renderedGroups.add(app.application_group_id);
                              const groupApps = sortedApplications.filter(a => a.application_group_id === app.application_group_id);
                              nodes.push(
                                <div key={app.application_group_id} className="space-y-2" data-testid={`app-group-${app.application_group_id.slice(0,8)}`}>
                                  <div className="flex items-center gap-2 px-2">
                                    <div className="h-px flex-1 bg-slate-200" />
                                    <span className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">Multi-Product Application</span>
                                    <div className="h-px flex-1 bg-slate-200" />
                                  </div>
                                  {groupApps.map(a => renderApp(a, true))}
                                </div>
                              );
                            } else {
                              nodes.push(renderApp(app, false));
                            }
                          });
                          return nodes;
                        })()}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="messages">
          <MessagingPanel
            userRole="broker"
            userId={user.id}
            preSelectedAppId={selectedMessageAppId}
            onAppSelect={setSelectedMessageAppId}
            onBack={previousContext || previousTab ? handleBackFromMessages : null}
          />
        </TabsContent>

        <TabsContent value="toolkit">
          <BrokerToolkit />
        </TabsContent>

        <TabsContent value="tasks">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">My Tasks</CardTitle>
            </CardHeader>
            <CardContent>
              <TaskManager />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="links">
          <LinksPanel />
        </TabsContent>

        {user.commissions_enabled && (
          <TabsContent value="commissions">
            <BrokerCommissions />
          </TabsContent>
        )}
        </div>
      </Tabs>

      {showNewApp && (
        <AIApplicationFlow onClose={() => setShowNewApp(false)} onSuccess={fetchData} />
      )}
      {resumeApp && (
        <AIApplicationFlow resumeApp={resumeApp} onClose={() => setResumeApp(null)} onSuccess={fetchData} />
      )}
      {reviewingApp && (
        <ApplicationReview
          application={reviewingApp}
          onClose={() => setReviewingApp(null)}
          onStatusUpdate={() => { setReviewingApp(null); fetchData(); }}
          onOpenMessages={openMessagesForApp}
        />
      )}
      <DashboardAIAssistant onAction={handleAIAction} />

      {/* MP Intake Link Modal */}
      {mpIntakeModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" data-testid="mp-intake-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center">
                <Link className="h-5 w-5 text-cyan-700" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Your Merchant Processing Intake Link</h3>
                <p className="text-xs text-slate-700">Share this link with potential clients to gather their pre-application info. Each submission creates a new Merchant Processing application under your account.</p>
              </div>
            </div>
            <div className="space-y-3">
              {mpIntakeLoading ? (
                <div className="flex items-center justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
              ) : mpIntakeError ? (
                <p className="text-sm text-red-600 font-medium">{mpIntakeError}</p>
              ) : (
                <>
                  <div className="bg-slate-50 border border-slate-300 rounded-lg p-3 break-all text-sm text-slate-900 font-mono" data-testid="mp-intake-link-text">{mpIntakeLink}</div>
                  <div className="flex gap-2">
                    <Button className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white" onClick={() => { navigator.clipboard.writeText(mpIntakeLink); alert('Link copied!'); }} data-testid="mp-intake-copy-btn">Copy Link</Button>
                    <Button variant="outline" className="flex-1" onClick={() => window.open(mpIntakeLink, '_blank')} data-testid="mp-intake-open-btn">Open Link</Button>
                  </div>
                </>
              )}
            </div>
            <div className="flex justify-end mt-4">
              <Button variant="ghost" onClick={() => setMpIntakeModalOpen(false)} data-testid="mp-intake-close-btn">Close</Button>
            </div>
          </div>
        </div>
      )}

      {/* Borrower Invite Confirmation */}
      {inviteConfirmAppId && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6" data-testid="invite-confirm-modal">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                <Mail className="h-5 w-5 text-teal-700" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800">Send Borrower Invite?</h3>
            </div>
            <p className="text-sm text-slate-600 mb-6">This will send an email to the borrower inviting them to upload their documents. Are you sure?</p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setInviteConfirmAppId(null)} data-testid="invite-cancel">Cancel</Button>
              <Button className="flex-1 bg-teal-700 hover:bg-teal-800 text-white" onClick={() => sendBorrowerInvite(inviteConfirmAppId)} data-testid="invite-confirm">Send Invite</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrokerDashboard;